import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useDebounceAsync from "hooks/useDebounceAsync";
import useTypedContext from "hooks/useTypedContext";
import { makeStringMatchesPredicate } from "utils/predicates";
import { StackSuggestions } from "constants/stack";

import useSearchStacksForNavigation from "../useSearchStacksForNavigation";

const useStackNameValidation = () => {
  const { onError } = useTypedContext(FlashContext);

  const { refetch: loadStacks } = useSearchStacksForNavigation({
    skip: true,
  });

  const checkNameUniqueness = useCallback(
    async (name: string): Promise<true | string> => {
      try {
        const response = await loadStacks({
          input: {
            predicates: [makeStringMatchesPredicate(StackSuggestions.Name, [name])],
            after: null,
            first: 1,
            fullTextSearch: name,
            orderBy: null,
            requestedPage: null,
          },
        });

        if (response?.data?.searchStacks.edges.length) {
          return "Stack with this name already exists";
        }

        return true;
      } catch (error) {
        onError(error);

        return "Something went wrong";
      }
    },
    [loadStacks, onError]
  );

  const debouncedCheckNameUniqueness = useDebounceAsync(checkNameUniqueness);

  const validateStackName = useCallback(
    async (value: string) => {
      if (!value?.trim()) {
        return "Stack name is required.";
      }

      return await debouncedCheckNameUniqueness(value);
    },
    [debouncedCheckNameUniqueness]
  );

  return validateStackName;
};

export default useStackNameValidation;
